import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './WidgetStyles.module.css';
import type { NegativeFeedbackReason } from '../types/feedback';
import { usePortalContainer } from './PortalContainerContext';

type FeedbackDialogProps = {
    isOpen: boolean;
    isSubmitting?: boolean;
    errorMessage?: string;
    onClose: () => void;
    onSubmit: (reason: NegativeFeedbackReason, comment: string) => Promise<void>;
};

type ReasonOption = {
    reason: NegativeFeedbackReason;
    label: string;
};

export function FeedbackDialog({
    isOpen,
    isSubmitting = false,
    errorMessage,
    onClose,
    onSubmit
}: FeedbackDialogProps) {
    const { t } = useTranslation();
    const portalContainer = usePortalContainer();
    const [selectedReason, setSelectedReason] = useState<NegativeFeedbackReason | null>(null);
    const [comment, setComment] = useState('');

    const reasons = useMemo<ReasonOption[]>(() => [
        { reason: 'IncorrectOrIncomplete', label: t('feedbackReasonIncorrectOrIncomplete') },
        { reason: 'NotWhatIAskedFor', label: t('feedbackReasonNotWhatIAskedFor') },
        { reason: 'SlowOrBuggy', label: t('feedbackReasonSlowOrBuggy') },
        { reason: 'StyleOrTone', label: t('feedbackReasonStyleOrTone') },
        { reason: 'SafetyOrLegalConcern', label: t('feedbackReasonSafetyOrLegalConcern') },
        { reason: 'Other', label: t('feedbackReasonOther') }
    ], [t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            onClose();
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen || !portalContainer) {
        return null;
    }

    const handleSubmit = async () => {
        if (!selectedReason) {
            return;
        }

        await onSubmit(selectedReason, comment);
    };

    return createPortal(
        <div className={styles.feedbackDialogOverlay} role="dialog" aria-modal="true" onClick={onClose}>
            <div className={styles.feedbackDialogCard} onClick={(event) => event.stopPropagation()}>
                <div className={styles.feedbackDialogHeader}>
                    <h3 className={styles.feedbackDialogTitle}>{t('feedbackDialogTitle')}</h3>
                    <button
                        className={styles.feedbackDialogClose}
                        type="button"
                        onClick={onClose}
                        aria-label={t('feedbackDialogClose')}
                        disabled={isSubmitting}
                    >
                        ×
                    </button>
                </div>
                <div className={styles.feedbackDialogReasonGrid}>
                    {reasons.map((option) => (
                        <button
                            key={option.reason}
                            type="button"
                            className={`${styles.feedbackReasonChip} ${selectedReason === option.reason ? styles.feedbackReasonChipActive : ''}`}
                            onClick={() => setSelectedReason(option.reason)}
                            disabled={isSubmitting}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <textarea
                    className={styles.feedbackDialogTextarea}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder={t('feedbackDialogCommentPlaceholder')}
                    maxLength={2000}
                    disabled={isSubmitting}
                />
                {errorMessage ? <p className={styles.feedbackDialogError}>{errorMessage}</p> : null}
                <div className={styles.feedbackDialogActions}>
                    <button
                        type="button"
                        className={styles.feedbackDialogPrimaryButton}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedReason}
                    >
                        {isSubmitting
                            ? t('feedbackDialogSubmitting')
                            : t('feedbackDialogSubmit')}
                    </button>
                </div>
            </div>
        </div>,
        portalContainer
    );
}
